import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import styled from 'styled-components'
import { Alert, AutoComplete, Modal, notification, Spin, Tooltip } from 'antd'

import type { WasteFacility } from 'portal/store/interfaces'
import { colours } from '@qflow/theme'

import { useModuleIds } from 'portal/lib/hooks'
import { imperial } from 'portal/lib/functions'
import { ColourButton, icons } from 'portal/lib/primitives'
import { LabeledValue } from 'portal/lib/sharedComponents/Select'
import { trackMixpanel } from 'portal/store/mixpanel/actions'
import { facilitiesLoad } from 'portal/store/wasteReferenceData/facilities/actions'
import {
  recordBulkUpdate,
  BulkUpdateResponse
} from 'portal/store/records/bulkUpdate/actions'
import { useContainerTypesLoader } from 'portal/components/RecordsModules/LineItemEdit/useContainerTypesLoader'
import {
  UpdateFieldSelector,
  FieldInput,
  FIELD_TYPES,
  type FieldType
} from './UpdateFieldSelector'
import { ClientKnownAddressModal } from 'portal/components/RecordsModules/Edit/ClientKnownAddressModal'
import type { KnownAddressResult } from 'portal/components/RecordsModules/Edit/ClientKnownAddressModalLink'

// Types
type RecordData = {
  // Waste fields
  facilityOperatorName?: string | null
  facilitySiteAddress?: string | null
  carrierName?: string | null
  wasteCode?: string | null
  numberOfItems?: number
  // Delivery fields
  knownSupplierName?: string | null
  tradeContractor?: string | null
  dispatchAddressKnown?: string | null
  deliveryAddressCoordinates?: { lat: number; lon: number } | null
}

type RecordDataMapping = Record<string, RecordData>

type RecordUpdateSectionProps = {
  onApply: () => void
  recordIds: string[]
  recordCount: number
  maxUpdate: number
  recordDataMapping: RecordDataMapping
}

// Utility functions
const isBlankValue = (value: any): boolean => {
  return value === '' || value === undefined || value === null
}

const hasBlankOrDifferentValues = (
  recordIds: string[],
  recordDataMapping: RecordDataMapping,
  fieldName: keyof RecordData
): boolean => {
  const hasBlankValues = recordIds.some(id =>
    isBlankValue(recordDataMapping[id]?.[fieldName])
  )

  if (hasBlankValues) {
    return true
  }
  if (recordIds.length < 2) {
    return false
  }

  const values = recordIds.map(id => recordDataMapping[id]?.[fieldName])
  return new Set(values).size > 1
}

// Unlike hasBlankOrDifferentValues, a blank value here acts as a wildcard -
// it matches whatever the other records' value is. But at least one record
// must have an actual (non-blank) value;
const hasConsistentOptionallyOccasionalBlankValues = (
  recordIds: string[],
  recordDataMapping: RecordDataMapping,
  fieldName: keyof RecordData
): boolean => {
  const nonBlankValues = recordIds
    .map(id => recordDataMapping[id]?.[fieldName])
    .filter(value => !isBlankValue(value))

  if (nonBlankValues.length === 0) {
    return false
  }

  return new Set(nonBlankValues).size === 1
}

const isContainerTypeListed = (
  value: string,
  allContainerTypes: any[]
): boolean => {
  if (!value) {
    return true
  }

  return allContainerTypes.some((ct: any) => {
    if (typeof ct === 'string') {
      return ct === value
    }
    return ct.name === value
  })
}

export function RecordUpdateSection({
  onApply,
  recordIds,
  recordCount,
  maxUpdate,
  recordDataMapping
}: RecordUpdateSectionProps) {
  const dispatch = useDispatch()
  const { accountId, projectId, moduleName } = useModuleIds()

  const isWaste = moduleName === 'waste'
  const isDelivery = moduleName === 'delivery'

  const getInitialFieldType = (): FieldType => {
    if (isWaste) return FIELD_TYPES.FACILITY_PERMIT
    if (isDelivery) return FIELD_TYPES.DISPATCH_ADDRESS
    return FIELD_TYPES.FACILITY_PERMIT
  }

  const [selectedFieldType, setSelectedFieldType] = useState<FieldType>(
    getInitialFieldType()
  )
  const [newValue, setNewValue] = useState<string>('')
  const [correspondingOperatorName, setCorrespondingOperatorName] =
    useState<string>('')
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [showAddressModal, setShowAddressModal] = useState(false)
  const [selectedAddress, setSelectedAddress] =
    useState<KnownAddressResult | null>(null)

  const {
    facilities,
    error
  }: {
    facilities: WasteFacility[]
    error: any
  } = useSelector((state: any) => state.facilities)

  const projectCountry = useSelector(
    (state: any) => state.projectDetails.modules?.country
  )
  const isImperial = imperial(projectCountry)

  const { items: allContainerTypes } = useContainerTypesLoader()

  // Validation helpers
  const hasBlankOrDifferentFacilityOperators = useMemo(
    () =>
      hasBlankOrDifferentValues(
        recordIds,
        recordDataMapping,
        'facilityOperatorName'
      ),
    [recordIds, recordDataMapping]
  )

  const hasBlankOrDifferentFacilitySiteAddresses = useMemo(
    () =>
      hasBlankOrDifferentValues(
        recordIds,
        recordDataMapping,
        'facilitySiteAddress'
      ),
    [recordIds, recordDataMapping]
  )

  // Requires non blank carriers to be consistent - they can't all be blank
  const hasQualifyingCarrierNames = useMemo(
    () =>
      hasConsistentOptionallyOccasionalBlankValues(
        recordIds,
        recordDataMapping,
        'carrierName'
      ),
    [recordIds, recordDataMapping]
  )

  const isFacilityPermitInvalid =
    (hasBlankOrDifferentFacilityOperators ||
      hasBlankOrDifferentFacilitySiteAddresses) &&
    !hasQualifyingCarrierNames

  const hasBlankOrDifferentCarrierNames = useMemo(
    () =>
      hasBlankOrDifferentValues(recordIds, recordDataMapping, 'carrierName'),
    [recordIds, recordDataMapping]
  )

  const hasBlankOrDifferentWasteCodes = useMemo(
    () => hasBlankOrDifferentValues(recordIds, recordDataMapping, 'wasteCode'),
    [recordIds, recordDataMapping]
  )

  const hasRecordsWithMultipleItems = useMemo(
    () => recordIds.some(id => (recordDataMapping[id]?.numberOfItems || 0) > 1),
    [recordIds, recordDataMapping]
  )

  // Delivery-specific validations
  const hasBlankOrDifferentSuppliers = useMemo(
    () =>
      hasBlankOrDifferentValues(
        recordIds,
        recordDataMapping,
        'knownSupplierName'
      ),
    [recordIds, recordDataMapping]
  )

  const hasBlankOrDifferentTradeContractors = useMemo(
    () =>
      hasBlankOrDifferentValues(
        recordIds,
        recordDataMapping,
        'tradeContractor'
      ),
    [recordIds, recordDataMapping]
  )

  // Get common supplier and trade contractor for bulk address lookup
  const { commonSupplierName, commonTradeContractorName } = useMemo(() => {
    if (recordIds.length === 0) {
      return {
        commonSupplierName: undefined,
        commonTradeContractorName: undefined
      }
    }
    const firstRecord = recordDataMapping[recordIds[0]]
    return {
      commonSupplierName: firstRecord?.knownSupplierName || undefined,
      commonTradeContractorName: firstRecord?.tradeContractor || undefined
    }
  }, [recordIds, recordDataMapping])

  // Get deduplicated delivery address coordinates for map display
  const deliveryAddressCoordinates = useMemo(() => {
    const coordinatesMap = new Map<string, { lat: number; lon: number }>()
    recordIds.forEach(id => {
      const coords = recordDataMapping[id]?.deliveryAddressCoordinates
      if (coords && coords.lat && coords.lon) {
        const key = `${coords.lat},${coords.lon}`
        coordinatesMap.set(key, coords)
      }
    })

    return Array.from(coordinatesMap.values())
  }, [recordIds, recordDataMapping])

  const containerTypeWarning = useMemo(() => {
    if (selectedFieldType !== FIELD_TYPES.CONTAINER_TYPE || !newValue) {
      return ''
    }

    return isContainerTypeListed(newValue, allContainerTypes)
      ? ''
      : 'Warning: Using an unlisted Container Type will remove weight/volume if ticket data is missing.'
  }, [selectedFieldType, newValue, allContainerTypes])

  // Effects
  useEffect(() => {
    dispatch(
      trackMixpanel('frontend_event_record_update_section_open', {
        account_id: accountId,
        project_id: projectId,
        record_type: moduleName,
        record_ids: recordIds,
        field_name: selectedFieldType
      })
    )
  }, [accountId, dispatch, moduleName, projectId, recordIds, selectedFieldType])

  useEffect(() => {
    if (error) {
      console.error(error)
    }
  }, [error])

  useEffect(() => {
    if (selectedFieldType === FIELD_TYPES.FACILITY_PERMIT) {
      // Debounce API calls to avoid loading on every keystroke
      const timer = setTimeout(() => {
        dispatch(facilitiesLoad(newValue, null))
      }, 300)

      return () => clearTimeout(timer)
    }
    return undefined
  }, [dispatch, newValue, selectedFieldType])

  // Memoized options
  const options = useMemo(() => {
    if (!facilities?.length) {
      return []
    }

    return facilities.map<LabeledValue>(facility => {
      return {
        value: facility.permitId,
        label: `${facility.permitId} (${facility.operator})`
      }
    })
  }, [facilities])

  const containerTypesOptions = useMemo(() => {
    if (!allContainerTypes?.length) {
      return []
    }

    return allContainerTypes
      .filter((ct: any) => {
        if (typeof ct === 'string') {
          return ct !== 'Unknown'
        }
        return ct.name !== 'Unknown'
      })
      .map((ct: any) => {
        if (typeof ct === 'string') {
          return {
            label: ct,
            value: ct,
            key: ct
          }
        }
        // Handle object with name and optional volumeM3
        const name = ct.name
        const label = ct.volumeM3
          ? isImperial
            ? `${name} (${(ct.volumeM3 * 1.30795).toFixed(2)} cu yd)`
            : `${name} (${ct.volumeM3} m³)`
          : name
        return {
          label,
          value: name,
          key: name
        }
      })
  }, [allContainerTypes, isImperial])

  // Event handlers
  const showConfirmationModal = useCallback(() => {
    setIsConfirmationModalOpen(true)
  }, [])

  const handleOnChange = useCallback(
    (value: string) => {
      setNewValue(value)
      if (selectedFieldType === FIELD_TYPES.FACILITY_PERMIT) {
        // Find the corresponding operator name based on the selected value
        const selectedFacility = facilities.find(
          facility => facility.permitId === value
        )
        setCorrespondingOperatorName(selectedFacility?.operator || '')
      }
    },
    [selectedFieldType, facilities]
  )

  const handleFieldTypeChange = useCallback((e: any) => {
    setSelectedFieldType(e.target.value)
    setNewValue('')
    setCorrespondingOperatorName('')
    setSelectedAddress(null)
  }, [])

  const handleAddressSubmit = useCallback((result: KnownAddressResult) => {
    setSelectedAddress(result)
    setNewValue(result.known)
    setShowAddressModal(false)
  }, [])

  const showSuccessNotification = useCallback(
    (response: BulkUpdateResponse) => {
      const {
        isFullySuccessful,
        recordsRequested,
        recordsSuccessfullyUpdated,
        failures
      } = response

      setIsUpdating(false)
      setIsConfirmationModalOpen(false)
      onApply()

      if (isFullySuccessful) {
        notification.success({
          message: `Successfully updated ${recordsSuccessfullyUpdated} record${recordsSuccessfullyUpdated !== 1 ? 's' : ''}`,
          duration: 5,
          placement: 'top'
        })
      } else {
        notification.warning({
          message: (
            <div>
              Bulk update partially completed:
              <br />
              {recordsSuccessfullyUpdated} of {recordsRequested} records updated
            </div>
          ),
          description:
            failures?.length > 0 ? (
              <div>
                <div>
                  {failures.length} failure{failures.length !== 1 ? 's' : ''}{' '}
                  occurred:
                </div>
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.25rem' }}>
                  {failures.map((failure, index) => (
                    <li key={index}>{failure}</li>
                  ))}
                </ul>
              </div>
            ) : undefined,
          duration: 0,
          placement: 'top'
        })
      }
    },
    [onApply]
  )

  const showFailureNotification = useCallback(() => {
    setIsUpdating(false)
    setIsConfirmationModalOpen(false)

    notification.error({
      message: 'Records failed to be updated.',
      description: 'Please reload the page and try again.',
      duration: 10,
      placement: 'top'
    })
  }, [])

  const onBulkUpdate = useCallback(() => {
    if (newValue === '') {
      return
    }

    // Limit the records to update to maxUpdate
    const recordsToUpdate = recordIds.slice(0, maxUpdate)

    // Set to true to allow overwriting existing values
    const allowOverwrite = true

    // For dispatch address, send the full address object as JSON
    const valueToSend =
      selectedFieldType === FIELD_TYPES.DISPATCH_ADDRESS && selectedAddress
        ? {
            newAddressEntered: selectedAddress.entered,
            newAddressKnown: selectedAddress.known,
            newAddressCoordinates: selectedAddress.coordinates
          }
        : newValue

    dispatch(
      recordBulkUpdate(
        accountId as string,
        projectId as string,
        moduleName,
        selectedFieldType,
        valueToSend,
        recordsToUpdate,
        showSuccessNotification,
        showFailureNotification,
        allowOverwrite
      )
    )
  }, [
    newValue,
    selectedAddress,
    recordIds,
    maxUpdate,
    dispatch,
    accountId,
    projectId,
    moduleName,
    selectedFieldType,
    showSuccessNotification,
    showFailureNotification
  ])

  const handleConfirmationOk = useCallback(() => {
    dispatch(
      trackMixpanel('frontend_event_record_update_section_confirm_ok', {
        account_id: accountId,
        project_id: projectId,
        record_type: moduleName,
        record_ids: recordIds,
        field_name: selectedFieldType
      })
    )
    setIsUpdating(true)
    onBulkUpdate()
  }, [
    dispatch,
    accountId,
    projectId,
    moduleName,
    recordIds,
    selectedFieldType,
    onBulkUpdate
  ])

  const handleConfirmationCancel = useCallback(() => {
    dispatch(
      trackMixpanel('frontend_event_record_update_section_confirm_cancel', {
        account_id: accountId,
        project_id: projectId,
        record_type: moduleName,
        record_ids: recordIds,
        field_name: selectedFieldType
      })
    )
    setIsConfirmationModalOpen(false)
  }, [dispatch, accountId, projectId, moduleName, recordIds, selectedFieldType])

  // Computed field label
  const getFieldLabel = (): string => {
    switch (selectedFieldType) {
      case FIELD_TYPES.FACILITY_PERMIT:
        return 'Facility Permit'
      case FIELD_TYPES.CONTAINER_TYPE:
        return 'Container Type'
      case FIELD_TYPES.DISPATCH_ADDRESS:
        return 'Dispatch Address'
      default:
        return ''
    }
  }

  const fieldLabel = getFieldLabel()

  // RETURNS
  // Delivery: Dispatch Address validation
  if (
    selectedFieldType === FIELD_TYPES.DISPATCH_ADDRESS &&
    (hasBlankOrDifferentSuppliers || hasBlankOrDifferentTradeContractors)
  ) {
    return (
      <StyledUpdateSection>
        <UpdateFieldSelector
          selectedFieldType={selectedFieldType}
          handleFieldTypeChange={handleFieldTypeChange}
          isImperial={isImperial}
          moduleName={moduleName}
        />
        <ErrorMessage>
          To bulk update Dispatch Address, all selected records must have the
          same:
          <ul>
            <li className={hasBlankOrDifferentSuppliers ? 'error' : ''}>
              Supplier (no blank values)
            </li>
            <li className={hasBlankOrDifferentTradeContractors ? 'error' : ''}>
              Trade Contractor (no blank values)
            </li>
          </ul>
        </ErrorMessage>
        <Alert
          message="Top tip: Use the table settings and filters to narrow down your selection"
          type="info"
          showIcon
          style={{
            marginTop: '0.5rem',
            padding: '0.5rem 0.5rem 0.5rem 0.75rem'
          }}
        />
      </StyledUpdateSection>
    )
  }

  // Waste: Facility Permit validation
  if (
    selectedFieldType === FIELD_TYPES.FACILITY_PERMIT &&
    isFacilityPermitInvalid
  ) {
    return (
      <StyledUpdateSection>
        <UpdateFieldSelector
          selectedFieldType={selectedFieldType}
          handleFieldTypeChange={handleFieldTypeChange}
          isImperial={isImperial}
          moduleName={moduleName}
        />
        <ErrorMessage>
          To bulk update Facility Permit, all selected records must have the
          same:
          <ul>
            <li className={hasBlankOrDifferentFacilityOperators ? 'error' : ''}>
              Facility operator (no blank values)
            </li>
            <li
              className={
                hasBlankOrDifferentFacilitySiteAddresses ? 'error' : ''
              }
            >
              Facility site address (no blank values)
            </li>
          </ul>
          <OrDivider>or the same:</OrDivider>
          <ul>
            <li className={hasQualifyingCarrierNames ? '' : 'error'}>
              Carrier (at least one non blank value)
            </li>
          </ul>
        </ErrorMessage>
        <Alert
          message="Top tip: Use the table settings and filters to narrow down your selection"
          type="info"
          showIcon
          style={{
            marginTop: '0.5rem',
            padding: '0.5rem 0.5rem 0.5rem 0.75rem'
          }}
        />
      </StyledUpdateSection>
    )
  }

  if (
    selectedFieldType === FIELD_TYPES.CONTAINER_TYPE &&
    (hasBlankOrDifferentCarrierNames ||
      hasBlankOrDifferentWasteCodes ||
      hasRecordsWithMultipleItems)
  ) {
    return (
      <StyledUpdateSection>
        <UpdateFieldSelector
          selectedFieldType={selectedFieldType}
          handleFieldTypeChange={handleFieldTypeChange}
          isImperial={isImperial}
          moduleName={moduleName}
        />
        <ErrorMessage>
          To bulk update Container Type, all selected records must have the
          same:
          <ul>
            <li className={hasBlankOrDifferentCarrierNames ? 'error' : ''}>
              Carrier (no blank values)
            </li>
            <li className={hasBlankOrDifferentWasteCodes ? 'error' : ''}>
              Waste Code (EWC) (no blank values)
            </li>
            <li className={hasRecordsWithMultipleItems ? 'error' : ''}>
              One waste item per record
            </li>
          </ul>
        </ErrorMessage>
        <Alert
          message="Top tip: Only single-item waste records can be bulk updated; filter for these using the 'All Waste Items' / 'Single Waste Item' toggle. Use the table settings and filters to narrow down your selection further."
          type="info"
          showIcon
          style={{
            marginTop: '0.5rem',
            padding: '0.5rem 0.5rem 0.5rem 0.75rem'
          }}
        />
      </StyledUpdateSection>
    )
  }

  return (
    <>
      <StyledUpdateSection>
        <UpdateFieldSelector
          selectedFieldType={selectedFieldType}
          handleFieldTypeChange={handleFieldTypeChange}
          isImperial={isImperial}
          moduleName={moduleName}
        />

        {selectedFieldType === FIELD_TYPES.DISPATCH_ADDRESS && (
          <>
            <FieldInput>
              <span>Dispatch address</span>
              {selectedAddress && <span>{selectedAddress.known}</span>}
              {selectedAddress ? (
                <Tooltip title="Edit address">
                  <ColourButton.Basic
                    type="button"
                    onClick={() => setShowAddressModal(true)}
                    style={{ maxHeight: '2.5rem' }}
                  >
                    <icons.Edit colour="currentColor" />
                  </ColourButton.Basic>
                </Tooltip>
              ) : (
                <ColourButton.Action
                  type="button"
                  onClick={() => setShowAddressModal(true)}
                  style={{ maxHeight: '2.5rem' }}
                >
                  Select Address
                </ColourButton.Action>
              )}
            </FieldInput>
            <EventUpdateMessage>
              Warning: This update will change the mileage for all selected
              records.
            </EventUpdateMessage>
          </>
        )}

        {selectedFieldType === FIELD_TYPES.FACILITY_PERMIT && (
          <>
            <FieldInput>
              <span>Facility permit</span>

              <AutoComplete
                allowClear
                placeholder="Enter a permit"
                value={newValue}
                options={options}
                popupMatchSelectWidth={false}
                onChange={handleOnChange}
                onSelect={handleOnChange}
                style={{ width: '54%' }}
              />
            </FieldInput>

            {newValue && (
              <FieldInput>
                <span>Operator</span>
                <span>
                  {correspondingOperatorName ? (
                    <span style={{ paddingLeft: '0.75rem' }}>
                      {correspondingOperatorName}
                    </span>
                  ) : newValue ? (
                    <EventUpdateMessage
                      style={{
                        width: '280px',
                        padding: '0.25rem 0.5rem 0.25rem 1.75rem',
                        backgroundPosition: '8px 7px'
                      }}
                    >
                      Operator not found in regulatory data
                    </EventUpdateMessage>
                  ) : (
                    ''
                  )}
                </span>
              </FieldInput>
            )}
            <EventUpdateMessage>
              Warning: Changing the 'Facility permit' will cause all Facility
              information to be updated.
            </EventUpdateMessage>
          </>
        )}

        {selectedFieldType === FIELD_TYPES.CONTAINER_TYPE && (
          <>
            <FieldInput>
              <span>Container type</span>
              <AutoComplete
                allowClear
                placeholder="Enter or select Container Type"
                value={newValue}
                options={containerTypesOptions}
                filterOption={(inputValue, option) =>
                  option?.value
                    ?.toString()
                    .toUpperCase()
                    .indexOf(inputValue.toUpperCase()) !== -1
                }
                onChange={handleOnChange}
                style={{ width: '54%' }}
              />
            </FieldInput>
            {containerTypeWarning && (
              <EventUpdateMessage>{containerTypeWarning}</EventUpdateMessage>
            )}
          </>
        )}

        <ButtonWrapper>
          <ColourButton.Action
            type="button"
            onClick={showConfirmationModal}
            disabled={
              selectedFieldType === FIELD_TYPES.DISPATCH_ADDRESS
                ? !selectedAddress
                : newValue === ''
            }
          >
            Apply to {recordCount} record{recordCount > 1 && 's'}
          </ColourButton.Action>
        </ButtonWrapper>
      </StyledUpdateSection>
      <Modal
        title="Updating records"
        open={isConfirmationModalOpen}
        onOk={handleConfirmationOk}
        onCancel={handleConfirmationCancel}
        closable={!isUpdating}
        maskClosable={!isUpdating}
        transitionName=""
        footer={[
          <ColourButton.Basic
            key="back"
            type="button"
            onClick={handleConfirmationCancel}
            disabled={isUpdating}
            style={{ display: 'inline-block', marginRight: '.75rem' }}
          >
            Cancel
          </ColourButton.Basic>,
          <ColourButton.Action
            key="submit"
            type="button"
            onClick={handleConfirmationOk}
            disabled={isUpdating}
            style={{ display: 'inline-block' }}
          >
            {isUpdating ? 'Updating...' : 'OK'}
          </ColourButton.Action>
        ]}
      >
        {isUpdating && (
          <div
            style={{
              textAlign: 'center',
              margin: '0.5rem 0'
            }}
          >
            <Spin size="large" />
            <div style={{ marginTop: '1rem', fontWeight: 500 }}>
              Updating records...
            </div>
          </div>
        )}
        {recordCount > maxUpdate && (
          <EventUpdateMessage style={{ marginBottom: '1rem' }}>
            <p>
              You have selected {recordCount} record
              {recordCount > 1 && 's'}. A maximum of {maxUpdate} records can be
              updated at a time.
            </p>
            <p>
              Would you like to update the first {maxUpdate} records of your
              selection? This operation cannot be undone.
            </p>
          </EventUpdateMessage>
        )}
        {recordCount <= maxUpdate && (
          <p>
            Update {recordCount} selected record{recordCount > 1 && 's'}?
          </p>
        )}

        <p>
          <LabelSpan>{fieldLabel}</LabelSpan> <b>{newValue}</b>
          {selectedFieldType === FIELD_TYPES.FACILITY_PERMIT && (
            <>
              <br />
              <LabelSpan>Operator</LabelSpan> <b>{correspondingOperatorName}</b>
            </>
          )}
        </p>

        <EventUpdateMessage style={{ marginBottom: '1rem' }}>
          <div>
            <b>This action cannot be undone.</b>
          </div>
          Please note that it may take a few minutes for the records to be
          updated.
        </EventUpdateMessage>
      </Modal>

      {selectedFieldType === FIELD_TYPES.DISPATCH_ADDRESS && (
        <ClientKnownAddressModal
          addressName="Dispatch"
          isOpen={showAddressModal}
          onCancel={() => setShowAddressModal(false)}
          onSubmit={handleAddressSubmit}
          otherAddressCoordinates={
            deliveryAddressCoordinates.length > 0
              ? deliveryAddressCoordinates
              : null
          }
          currentAddressCoordinates={null}
          otherAddressName={'Delivery'}
          supplierName={commonSupplierName}
          tradeContractorName={commonTradeContractorName}
        />
      )}
    </>
  )
}

const StyledUpdateSection = styled.div`
  padding: 1rem;
  background-color: ${colours.CONTROL_PANEL_GREY};
  border-radius: 8px;
  margin: 1rem 0 0.5rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  max-width: 542px;

  h3 {
    margin: 0;
    font-size: 0.875rem;
    font-weight: 600;
  }
`

const ErrorMessage = styled.div`
  color: ${colours.ERROR};
  font-weight: 600;
  margin: 0 auto;

  ul {
    color: ${colours.CORE_NAVY};
    list-style: none;
    margin: 0.5rem 0 0 0;
    padding-left: 4rem;

    li {
      margin: 0.25rem 0;
    }
    li:before {
      color: ${colours.SUCCESS};
      content: '✓';
      padding-right: 0.5rem;
    }
    li.error {
      &:before {
        color: ${colours.ERROR};
        content: '✗';
      }
    }
  }
`

const OrDivider = styled.div`
  color: ${colours.ERROR};
  font-weight: 600;
  text-align: left;
  margin: 0.25rem 0;
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;

  button {
    width: fit-content;
  }
`

export const EventUpdateMessage = styled.div`
  color: ${colours.ORANGE_TEXT};
  font-size: 13px;
  font-weight: 500;
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M0.583008 12.5418H13.4163L6.99967 1.4585L0.583008 12.5418ZM7.58301 10.7918H6.41634V9.62516H7.58301V10.7918ZM7.58301 8.4585H6.41634V6.12516H7.58301V8.4585Z" fill="%23DA5E30" /></svg>');
  background-position: 8px 12px;
  background-repeat: no-repeat;
  margin: 0;
  width: 100%;
  padding: 0.5rem 0.625rem 0.5rem 1.75rem;
  border: 1px solid ${colours.ORANGE};
  border-radius: 5px;
  background-color: #fef7f3;

  > p:last-child {
    margin-bottom: 0;
  }
`

const LabelSpan = styled.span`
  min-width: 7.5rem;
  display: inline-block;
`
